/**
 * useCalendarAPI Hook
 * React Query hooks for calendar API operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  calendarService,
  CalendarEvent,
  CalendarEventListItem,
  CalendarEventCreateData,
  CalendarEventUpdateData,
  CalendarFilters,
  RSVPData,
  ChangeStatusData,
  CompleteEventData,
} from '@/services/api/calendarService';
import { toast } from 'sonner';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const calendarKeys = {
  all: ['calendar'] as const,
  lists: () => [...calendarKeys.all, 'list'] as const,
  list: (filters: CalendarFilters) => [...calendarKeys.lists(), filters] as const,
  details: () => [...calendarKeys.all, 'detail'] as const,
  detail: (id: string) => [...calendarKeys.details(), id] as const,
  upcoming: (days?: number) => [...calendarKeys.all, 'upcoming', days] as const,
  today: () => [...calendarKeys.all, 'today'] as const,
  calendarView: (month: string, userId?: string, companyId?: string, projectId?: string) =>
    [...calendarKeys.all, 'view', month, userId, companyId, projectId] as const,
  leadHistory: (leadId: string) => [...calendarKeys.all, 'lead', leadId] as const,
  availability: (params: { user_ids: string[]; start_date: string; end_date: string }) =>
    [...calendarKeys.all, 'availability', params] as const,
};

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Get calendar events with filters
 */
export const useCalendarEventsList = (filters?: CalendarFilters) => {
  return useQuery({
    queryKey: calendarKeys.list(filters || {}),
    queryFn: () => calendarService.getEvents(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Get single calendar event by ID
 */
export const useCalendarEvent = (id: string, enabled = true) => {
  return useQuery({
    queryKey: calendarKeys.detail(id),
    queryFn: () => calendarService.getEventById(id),
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Get upcoming events (next N days)
 */
export const useUpcomingEvents = (days = 7) => {
  return useQuery({
    queryKey: calendarKeys.upcoming(days),
    queryFn: () => calendarService.getUpcoming(days),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Get today's events
 */
export const useTodayEvents = () => {
  return useQuery({
    queryKey: calendarKeys.today(),
    queryFn: () => calendarService.getToday(),
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Get calendar view (events grouped by date for a month)
 *
 * @param month - Month in YYYY-MM format
 * @param userId - Optional user ID to filter events (for tenant users, defaults to self)
 * @param companyId - Optional company ID for platform users to filter by tenant
 * @param projectId - Optional project ID to filter events
 */
export const useCalendarView = (
  month: string,
  userId?: string,
  companyId?: string,
  projectId?: string
) => {
  return useQuery({
    queryKey: calendarKeys.calendarView(month, userId, companyId, projectId),
    queryFn: () => calendarService.getCalendarView({
      month,
      user_id: userId,
      company: companyId,
      project: projectId,
    }),
    enabled: !!month,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Get event history for a lead
 */
export const useLeadEventHistory = (leadId: string, enabled = true) => {
  return useQuery({
    queryKey: calendarKeys.leadHistory(leadId),
    queryFn: () => calendarService.getLeadHistory(leadId),
    enabled: enabled && !!leadId,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Get availability (busy times)
 */
export const useAvailability = (params: {
  user_ids: string[];
  start_date: string;
  end_date: string;
  duration_minutes?: number;
}, enabled = true) => {
  return useQuery({
    queryKey: calendarKeys.availability(params),
    queryFn: () => calendarService.getAvailability(params),
    enabled: enabled && params.user_ids.length > 0 && !!params.start_date && !!params.end_date,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Create a new calendar event
 */
export const useCreateCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CalendarEventCreateData) => calendarService.createEvent(data),
    onSuccess: (newEvent) => {
      // Invalidate all calendar queries
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      toast.success('Evento creado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al crear evento';
      toast.error(message);
    },
  });
};

/**
 * Update an existing calendar event
 */
export const useUpdateCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CalendarEventUpdateData }) =>
      calendarService.updateEvent(id, data),
    onSuccess: (updatedEvent) => {
      // Update the specific event in cache
      queryClient.setQueryData(calendarKeys.detail(updatedEvent.id), updatedEvent);
      // Invalidate all calendar queries (including calendarView for DashboardCalendar)
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      toast.success('Evento actualizado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al actualizar evento';
      toast.error(message);
    },
  });
};

/**
 * Delete a calendar event (soft delete)
 */
export const useDeleteCalendarEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => calendarService.deleteEvent(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: calendarKeys.detail(deletedId) });
      // Invalidate all calendar queries (including calendarView for DashboardCalendar)
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      toast.success('Evento eliminado exitosamente');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al eliminar evento';
      toast.error(message);
    },
  });
};

/**
 * RSVP to an event invitation
 */
export const useRSVP = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: RSVPData }) =>
      calendarService.rsvp(eventId, data),
    onSuccess: (result, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: calendarKeys.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: calendarKeys.lists() });

      const statusMessages: Record<string, string> = {
        accepted: 'Has aceptado la invitación',
        declined: 'Has rechazado la invitación',
        tentative: 'Has respondido tentativamente',
      };
      toast.success(statusMessages[result.response_status] || 'Respuesta enviada');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al responder invitación';
      toast.error(message);
    },
  });
};

/**
 * Change event status
 */
export const useChangeEventStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: ChangeStatusData }) =>
      calendarService.changeStatus(eventId, data),
    onSuccess: (updatedEvent) => {
      queryClient.setQueryData(calendarKeys.detail(updatedEvent.id), updatedEvent);
      queryClient.invalidateQueries({ queryKey: calendarKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.upcoming() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.today() });
      toast.success('Estado del evento actualizado');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al cambiar estado';
      toast.error(message);
    },
  });
};

/**
 * Complete event with result
 */
export const useCompleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: CompleteEventData }) =>
      calendarService.completeEvent(eventId, data),
    onSuccess: (updatedEvent) => {
      queryClient.setQueryData(calendarKeys.detail(updatedEvent.id), updatedEvent);
      queryClient.invalidateQueries({ queryKey: calendarKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.upcoming() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.today() });

      const resultMessages: Record<string, string> = {
        positive: 'Evento completado - Resultado positivo',
        neutral: 'Evento completado - Resultado neutral',
        negative: 'Evento completado - Resultado negativo',
        reschedule: 'Evento marcado para reagendar',
        no_show: 'Evento marcado como no asistió',
      };
      toast.success(resultMessages[updatedEvent.meeting_result || ''] || 'Evento completado');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al completar evento';
      toast.error(message);
    },
  });
};

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Combined hook for common calendar operations
 */
export const useCalendarOperations = () => {
  const createEvent = useCreateCalendarEvent();
  const updateEvent = useUpdateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();
  const changeStatus = useChangeEventStatus();
  const completeEvent = useCompleteEvent();
  const rsvp = useRSVP();

  return {
    createEvent,
    updateEvent,
    deleteEvent,
    changeStatus,
    completeEvent,
    rsvp,
    isLoading:
      createEvent.isPending ||
      updateEvent.isPending ||
      deleteEvent.isPending ||
      changeStatus.isPending ||
      completeEvent.isPending ||
      rsvp.isPending,
  };
};
